import { DateTime } from 'luxon';
import { action, computed, makeObservable, runInAction } from 'mobx';

import { timelogsApi } from '@/api/timelogsApi';
import { yandexTrackerApi } from '@/api/yandexTrackerApi';
import { ViewTable } from '@/configs/ViewTable/viewTable';
import { DraftTimelogStore } from '@/features/draftTimelogs/model';
import { LogsMode } from '@/features/logsMode';
import { viewSettingsStore } from '@/features/viewSettings';
import { LoadingStageModel } from '@/models/LoadingStageModel';
import { ValueModel } from '@/models/ValueModel';
import { TimeLogStore } from '@/pages/TimesheetsPage/model/TimeLogStore';
import { YandexTaskInfo } from '@/types/yandex';
import { formatDurationRussian } from '@/utils/formatDurationRussian';
import { transformTimelogForStats, transformTimelogsTaskForStats } from '@/utils/transformTimelog';

import { EstimationVariancePoint, uniqueTaskKeysFromLogs } from './estimationVariance';
import {
  createEstimationVarianceCache,
  EstimationVarianceDeps,
  loadEstimationVariance,
} from './loadEstimationVariance';
import {
  buildEpicFieldProjectChartDataFromLogs,
  buildParentProjectChartDataFromLogs,
  buildProjectChartDataFromLogs,
  OTHER_DISTRIBUTION_LABEL,
  ProjectDistributionMode,
  takeTopWithOther,
} from './projectDistributionChartData';
import { DonutData, TProjectDistributionMode } from './types';

const STATS_DEPS_LEGACY: EstimationVarianceDeps = {
  getTask: (params) => yandexTrackerApi.getTask(params),
  getWorkLogs: (params) => yandexTrackerApi.getWorkLogs(params),
};

const STATS_DEPS_V2: EstimationVarianceDeps = {
  getTask: async ({ taskKey }) => transformTimelogsTaskForStats(await timelogsApi.getTask(taskKey)),
  getWorkLogs: async (params) =>
    (await timelogsApi.searchTaskTimelogs(params)).map(transformTimelogForStats),
};

export class TimeStatsModel {
  private readonly timeLogStore: TimeLogStore;
  private readonly draftTimelogStore: DraftTimelogStore;

  private readonly _estimationPoints = new ValueModel<EstimationVariancePoint[]>([]);
  private readonly _estimationFailedCount = new ValueModel(0);
  private readonly _estimationSkippedCount = new ValueModel(0);
  readonly estimationLoadingStage = new LoadingStageModel();

  readonly projectDistributionMode = new ValueModel<TProjectDistributionMode>(
    ProjectDistributionMode.project,
  );

  private estimationCache = createEstimationVarianceCache();
  private estimationAbortController: AbortController | null = null;
  private estimationLoadId = 0;
  private lastEstimationLoadKey: string | null = null;
  private lastLogsRevision: string | null = null;
  private lastWorklogCacheTaskKeys: string[] = [];

  constructor(timeLogStore: TimeLogStore, draftTimelogStore: DraftTimelogStore) {
    this.timeLogStore = timeLogStore;
    this.draftTimelogStore = draftTimelogStore;

    makeObservable(this, {
      totalTime: computed,
      logMinutes: computed,
      draftMinutes: computed,
      combinedTotalMinutes: computed,
      logChartData: computed,
      projectChartData: computed,
      parentProjectChartData: computed,
      epicFieldProjectChartData: computed,
      projectDistributionChartData: computed,
      estimationPoints: computed,
      estimationFailedCount: computed,
      estimationSkippedCount: computed,
      statsPeriod: computed,
      loadEstimationVariance: action.bound,
      resetEstimationSession: action.bound,
    });
  }

  private get logsForCurrentPeriod() {
    const timesheet = this.timeLogStore.filterData;
    const currentDate = this.timeLogStore.currentDate;

    if (!timesheet || !currentDate) return [];

    const allLogs = Object.values(timesheet.days).flat();

    if (viewSettingsStore.view === ViewTable.Week) {
      return allLogs;
    }

    const currentMonth = currentDate.month;

    return allLogs.filter((log) => {
      const logDate = DateTime.fromISO(log.date).month;

      return logDate === currentMonth;
    });
  }

  get statsPeriod(): { start: DateTime; end: DateTime } | null {
    const currentDate = this.timeLogStore.currentDate;

    if (!currentDate?.isValid) {
      return null;
    }

    const view = viewSettingsStore.view;

    return {
      start: currentDate.startOf(view),
      end: currentDate.endOf(view),
    };
  }

  get totalTime() {
    return this.combinedTotalMinutes;
  }

  /** Залогированное время (без черновиков) */
  get logMinutes() {
    const timesheet = this.timeLogStore.filterData;

    if (!timesheet) return 0;

    return timesheet.totalMinutes;
  }

  /** Черновики за загруженный период (только при просмотре себя) */
  get draftMinutes() {
    if (!this.draftTimelogStore.isViewingSelf) return 0;

    return this.draftTimelogStore.totalDraftMinutes;
  }

  get combinedTotalMinutes() {
    return this.logMinutes + this.draftMinutes;
  }

  get logChartData() {
    const logs = this.logsForCurrentPeriod;

    if (logs.length === 0) return [];

    const taskMap: Record<string, { taskKey: string; title: string; minutes: number }> = {};

    logs.forEach((log) => {
      if (!taskMap[log.taskKey]) {
        taskMap[log.taskKey] = {
          taskKey: log.taskKey,
          title: log.taskTitle,
          minutes: 0,
        };
      }

      taskMap[log.taskKey].minutes += log.durationMinutes;
    });

    const sorted = Object.values(taskMap).sort((a, b) => b.minutes - a.minutes);
    const { items, otherMinutes } = takeTopWithOther(sorted);

    if (otherMinutes > 0) {
      items.push({ taskKey: '', title: OTHER_DISTRIBUTION_LABEL, minutes: otherMinutes });
    }

    const totalMinutes = items.reduce((sum, item) => sum + item.minutes, 0);

    return items.map((item) => ({
      taskKey: item.taskKey,
      name: item.title,
      value: item.minutes,
      label: formatDurationRussian(item.minutes),
      percent: ((item.minutes / totalMinutes) * 100).toFixed(1) + '%',
    }));
  }

  get projectChartData(): DonutData[] {
    return buildProjectChartDataFromLogs(this.logsForCurrentPeriod);
  }

  get parentProjectChartData(): DonutData[] {
    void this.estimationPoints;
    void this.estimationLoadingStage.isLoading;

    return buildParentProjectChartDataFromLogs(this.logsForCurrentPeriod, (taskKey) =>
      this.getTaskInfoByKey(taskKey),
    );
  }

  get epicFieldProjectChartData(): DonutData[] {
    void this.estimationPoints;
    void this.estimationLoadingStage.isLoading;

    return buildEpicFieldProjectChartDataFromLogs(this.logsForCurrentPeriod, (taskKey) =>
      this.getTaskInfoByKey(taskKey),
    );
  }

  get projectDistributionChartData(): DonutData[] {
    if (this.projectDistributionMode.value === ProjectDistributionMode.parent) {
      return this.parentProjectChartData;
    }

    if (this.projectDistributionMode.value === ProjectDistributionMode.epic) {
      return this.epicFieldProjectChartData;
    }

    return this.projectChartData;
  }

  private getTaskInfoByKey(taskKey: string): YandexTaskInfo | undefined {
    return this.estimationCache.tasks.get(taskKey);
  }

  get estimationPoints() {
    return this._estimationPoints.value;
  }

  get estimationFailedCount() {
    return this._estimationFailedCount.value;
  }

  get estimationSkippedCount() {
    return this._estimationSkippedCount.value;
  }

  private getLogsRevision() {
    return this.logsForCurrentPeriod
      .map((log) => `${log.id}:${log.taskKey}:${log.durationMinutes}:${log.date}`)
      .sort()
      .join(',');
  }

  private getEstimationLoadKey(
    selectedTrackerUserId: string,
    periodStart: DateTime,
    periodEnd: DateTime,
  ) {
    const taskKeys = uniqueTaskKeysFromLogs(this.logsForCurrentPeriod).join(',');

    return `${selectedTrackerUserId}|${periodStart.toISO()}|${periodEnd.toISO()}|${taskKeys}|${this.getLogsRevision()}`;
  }

  private invalidateWorklogCacheForLogChanges() {
    const logsRevision = this.getLogsRevision();

    if (this.lastLogsRevision === logsRevision) {
      return;
    }

    const currentTaskKeys = uniqueTaskKeysFromLogs(this.logsForCurrentPeriod);
    const taskKeysToInvalidate = new Set([...this.lastWorklogCacheTaskKeys, ...currentTaskKeys]);

    taskKeysToInvalidate.forEach((taskKey) => {
      this.estimationCache.worklogs.delete(taskKey);
    });

    this.lastLogsRevision = logsRevision;
    this.lastWorklogCacheTaskKeys = currentTaskKeys;
  }

  resetEstimationSession() {
    this.estimationAbortController?.abort();
    this.estimationAbortController = null;
    this.estimationLoadId += 1;
    this.lastEstimationLoadKey = null;
    this.lastLogsRevision = null;
    this.lastWorklogCacheTaskKeys = [];
    this._estimationPoints.change([]);
    this._estimationFailedCount.change(0);
    this._estimationSkippedCount.change(0);
    this.estimationLoadingStage.reset();
  }

  async loadEstimationVariance() {
    const period = this.statsPeriod;
    const selectedTrackerUserId = this.timeLogStore.selectedUserId;

    if (!period || !selectedTrackerUserId) {
      this.resetEstimationSession();
      this.estimationLoadingStage.success();

      return;
    }

    const loadKey = this.getEstimationLoadKey(selectedTrackerUserId, period.start, period.end);

    if (
      this.lastEstimationLoadKey === loadKey &&
      (this.estimationLoadingStage.isLoading || this.estimationLoadingStage.isSuccess)
    ) {
      return;
    }

    this.invalidateWorklogCacheForLogChanges();

    this.estimationAbortController?.abort();
    this.estimationAbortController = new AbortController();
    const { signal } = this.estimationAbortController;
    const loadId = ++this.estimationLoadId;
    this.lastEstimationLoadKey = loadKey;

    this.estimationLoadingStage.loading();

    try {
      const result = await loadEstimationVariance({
        logs: this.logsForCurrentPeriod,
        selectedTrackerUserId,
        periodStart: period.start,
        periodEnd: period.end,
        cache: this.estimationCache,
        signal,
        ...(this.timeLogStore.mode === LogsMode.v2 ? STATS_DEPS_V2 : STATS_DEPS_LEGACY),
      });

      if (loadId !== this.estimationLoadId || signal.aborted) {
        return;
      }

      runInAction(() => {
        this._estimationPoints.change(result.points);
        this._estimationFailedCount.change(result.failedCount);
        this._estimationSkippedCount.change(result.skippedCount);
        this.estimationLoadingStage.success();
      });
    } catch (error) {
      if (loadId !== this.estimationLoadId || signal.aborted) {
        return;
      }

      runInAction(() => {
        this._estimationPoints.change([]);
        this._estimationFailedCount.change(this.logsForCurrentPeriod.length);
        this._estimationSkippedCount.change(0);
        this.lastEstimationLoadKey = null;
        this.estimationLoadingStage.error();
      });

      console.error('Failed to load estimation variance stats', error);
    }
  }
}
